import Button from '@ui-kit/components/Button';
import { ModalActions } from '@ui-kit/components/Modal/Modal';
import { RadioGroup, RadioGroupItem } from '@ui-kit/components/radios/RadioGroup/RadioGroup';
import { useEffect, useEffectEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useTerminateAccountMutation from '@/components/ModalRenderer/components/components/AccountTerminationForm/useTerminateAccountMutation';
import Textarea from '@/components/Textarea';
import { showErrorToast } from '@/components/Toast';
import type { TerminationReason } from '@/constants/TerminationReasons';
import { TerminationReasons } from '@/constants/TerminationReasons';
import usePendingTransactionPolling from '@/domains/products/hooks/usePendingTransactionPolling';
import useModal from '@/hooks/useModal';
import { routeMap } from '@/routes/routeMap';

type FormValues = {
  terminationReason?: TerminationReason;
  comment: string;
};

const TERMINATION_REASON_OPTIONS: TerminationReason[] = Object.values(TerminationReasons);

const AccountTerminationForm = ({ buttonText }: { buttonText: string }) => {
  const { t } = useTranslation(['FormErrors', 'Common', 'UserProfile', 'ErrorToast']);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const { closeModal } = useModal();
  const navigate = useNavigate();

  const {
    register,
    watch,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    mode: 'all',
    defaultValues: { terminationReason: undefined, comment: '' },
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch; refactor out of i18n MR scope
  const terminationReason = watch('terminationReason');

  const { mutate: terminateAccount, isPending: isTerminateAccountPending } = useTerminateAccountMutation();

  const { data: pendingTransaction, isSuccess: isPendingTransactionSuccess } = usePendingTransactionPolling();

  const handleTerminateAccount = useEffectEvent(() => {
    setIsFormSubmitted(false);
    const { terminationReason, comment } = getValues();
    if (terminationReason) {
      terminateAccount({ terminationReason, comment: comment || undefined });
    }
  });

  useEffect(() => {
    if (!isFormSubmitted || !isPendingTransactionSuccess) {
      return;
    }

    if (!pendingTransaction.pendingTransaction && !pendingTransaction.delayed) {
      handleTerminateAccount();
    }

    if (pendingTransaction.delayed) {
      closeModal();
      showErrorToast(t('ErrorToast:transactionDelayed'));
      void navigate(routeMap.userProfile.root, { replace: true });
    }
    // TODO: Remove this comment once eslint is updated to support useEffectEvent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormSubmitted, isPendingTransactionSuccess, pendingTransaction]);

  const onSubmit = () => {
    setIsFormSubmitted(true);
  };

  const isTerminating = isTerminateAccountPending || isFormSubmitted;

  return (
    <form className="mt-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4">
        <fieldset>
          <legend className="text-body-semibold mb-4">{t('UserProfile:termination.reasonPrompt')}</legend>

          <RadioGroup className="flex flex-col gap-2">
            {TERMINATION_REASON_OPTIONS.map(reason => (
              <RadioGroupItem
                key={reason}
                {...register('terminationReason', {
                  required: t('FormErrors:required'),
                })}
                value={reason}
                disabled={isTerminating}
                error={!!errors.terminationReason}
              >
                {t(`UserProfile:termination.reasonOption_${reason}`)}
              </RadioGroupItem>
            ))}
          </RadioGroup>
        </fieldset>

        <Textarea
          {...register('comment', {
            maxLength: { value: 300, message: t('FormErrors:maxLength', { length: 300 }) },
          })}
          aria-label={t('Common:feedback')}
          placeholder={t('UserProfile:termination.formPlaceholder')}
          errorMessage={errors.comment?.message}
          disabled={!terminationReason || isTerminating}
          rows={3}
        />
      </div>

      <ModalActions className="mt-8!">
        <Button type="submit" variant="destructive" loading={isTerminating} disabled={!isValid}>
          {buttonText}
        </Button>
      </ModalActions>
    </form>
  );
};

export default AccountTerminationForm;
